import React from "react";
import { useTranslation } from "react-i18next";
import { truncateCommaSeparatedString } from "../../components/common/utlity/utility";
import { AWS_URL } from "../../services/http.service";
import { Country, State } from "country-state-city";
import numberToWord from "../../components/common/utlity/invoiceword";

const InvoiceHeader = ({ businessName, selectedColor, bussiness }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${selectedColor} text-center py-4 w-1/2 text-left text-white`}
    >
      <h1 className="text-2xl font-bold">{t("NEWINVOICE.TAX_INVOICE")}</h1>
      <div className="flex items-center mt-1 text-sm">
        {/* Logo */}
        <div className="w-5 h-5 mr-2 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 shadow">
          <img
            src={
              bussiness?.businesslogo
                ? AWS_URL + bussiness.businesslogo
                : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT3dSEtDvpd9KAn4SdFJVoL0K1lj4Xfc-wVWQ&s"
            }
            alt="Business Logo"
            className="object-contain w-full h-full"
            //crossOrigin="anonymous"
          />
        </div>

        {/* Business Name */}
        <span>{businessName || "Business Name"}</span>
      </div>
    </div>
  );
};

const InvoiceDetails = ({
  businessName,
  phone,
  invoiceId,
  customerName,
  mobile,
  saleDate,
  selectedColor,
  bussiness,
  item,
}) => {
  const { t } = useTranslation();
  const formatDate = (str) => {
    if (!str) return "-";
    const date = new Date(str);
    if (isNaN(date)) return str;
    return date.toLocaleDateString();
  };
  const businessCountry = bussiness?.country
    ? Country.getCountryByCode(bussiness.country)
    : null;
  const businessState = bussiness?.state
    ? State.getStateByCodeAndCountry(bussiness.state, bussiness.country)
    : null;
  const customerCountry = item?.country
    ? Country.getCountryByCode(item.country)
    : null;
  const customerState = item?.state
    ? State.getStateByCodeAndCountry(item.state, item.country)
    : null;

  return (
    <div className="p-6 border-b border-gray-300">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
        <div className="space-y-2">
          <div className="font-semibold">{businessName}</div>
          <div>
            {t("NEWINVOICE.PHONE")}: {phone}
          </div>
          <span className="text-xs block">
            GSTIN: {bussiness?.gstNumber || "N/A"} | Email:{" "}
            {bussiness?.email || "N/A"}
          </span>
          <span className="text-xs block">
            {bussiness?.firstLine
              ? `${bussiness.firstLine}, ${businessState?.name || ""}, ${
                  businessCountry?.name || ""
                }`
              : "Address not available"}
          </span>
        </div>
        <div className="space-y-2 text-right">
          <div>
            {t("NEWINVOICE.NO")}: {invoiceId || "-"}
          </div>
          <div>
            {t("NEWINVOICE.DATE")} {formatDate(saleDate)}
          </div>
        </div>
      </div>
      <div className="border-t border-gray-300 mt-4 pt-2 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
        <div>
          <div className="font-semibold">{t("NEWINVOICE.BILL_TO")}:</div>
          <div>{customerName || "-"}</div>
          <div>
            {t("NEWINVOICE.CONTACT_NO")}: {mobile || "-"}
          </div>
          <div>
            {item.address
              ? `${item.address}, ${customerState?.name || ""}, ${
                  customerCountry?.name || ""
                }`
              : "Address not available"}
          </div>
        </div>
      </div>
    </div>
  );
};

const InvoiceTable = ({ items, selectedColor }) => {
  const safeItems = items || [];
  const { t } = useTranslation();
  const totalQuantity = safeItems.reduce(
    (acc, item) => acc + (item.quantity || 0),
    0
  );
  const totalGST = safeItems.reduce(
    (acc, item) =>
      acc +
      (item.salePrice || 0) * (item.quantity || 0) * ((item.igst || 0) / 100),
    0
  );
  const totalAmount = safeItems.reduce(
    (acc, item) => acc + (item.finalPrice || 0),
    0
  );

  const currencySymbol = "₹";

  const formatCurrency = (val) =>
    `${currencySymbol} ${Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  if (!safeItems.length) {
    return (
      <div className="px-6 py-4 text-center text-gray-500">
        No items to display
      </div>
    );
  }

  return (
    <div className="overflow-x-auto px-6">
      <table className="min-w-full border border-gray-400 border-collapse text-sm">
        <thead className={`${selectedColor} text-white`}>
          <tr>
            <th className="px-4 py-2 text-left border border-gray-400">#</th>
            <th className="px-4 py-2 text-left border border-gray-400">
              {t("NEWINVOICE.ITEM_NAME")}
            </th>
            <th className="px-4 py-2 text-left border border-gray-400">
              {t("NEWINVOICE.HSN_SAC")}
            </th>
            <th className="px-4 py-2 text-center border border-gray-400">
              {t("NEWINVOICE.QTY")}
            </th>
            <th className="px-4 py-2 text-right border border-gray-400">
              {t("NEWINVOICE.PRICE_PER_UNIT")}
            </th>
            <th className="px-4 py-2 text-right border border-gray-400">
              {t("NEWINVOICE.GST")}
            </th>
            <th className="px-4 py-2 text-right border border-gray-400">
              {t("NEWINVOICE.AMOUNT")}
            </th>
          </tr>
        </thead>
        <tbody>
          {safeItems.map((item, idx) => {
            const gstAmount =
              (item.salePrice || 0) *
              (item.quantity || 0) *
              ((item.igst || 0) / 100);
            return (
              <tr key={idx} className="border-t border-gray-400 text-sm">
                <td className="px-4 py-2 border border-gray-400 text-center">
                  {idx + 1}
                </td>
                <td className="px-4 py-2 border border-gray-400 font-semibold">
                  {item.productName || item.description || "-"}
                </td>
                <td className="px-4 py-2 border border-gray-400">
                  {truncateCommaSeparatedString(item.hsnNumber) ||
                    item.hsn ||
                    "N/A"}
                </td>
                <td className="px-4 py-2 border border-gray-400 text-center">
                  {item.quantity || 0}
                </td>
                <td className="px-4 py-2 border border-gray-400 text-right">
                  {formatCurrency(item.salePrice)}
                </td>
                <td className="px-4 py-2 border border-gray-400 text-right">
                  {formatCurrency(gstAmount)} ({item.igst || 0}%)
                </td>
                <td className="px-4 py-2 border border-gray-400 text-right">
                  {formatCurrency(item.finalPrice)}
                </td>
              </tr>
            );
          })}
          <tr
            className={`${selectedColor} text-white font-semibold border-t border-gray-400`}
          >
            <td className="px-4 py-2 border border-gray-400"></td>
            <td className="px-4 py-2 border border-gray-400">
              {t("NEWINVOICE.TOTAL")}
            </td>
            <td className="px-4 py-2 border border-gray-400"></td>
            <td className="px-4 py-2 border border-gray-400 text-center">
              {totalQuantity}
            </td>
            <td className="px-4 py-2 border border-gray-400"></td>
            <td className="px-4 py-2 border border-gray-400 text-right">
              {formatCurrency(totalGST)}
            </td>
            <td className="px-4 py-2 border border-gray-400 text-right">
              {formatCurrency(totalAmount)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const TotalsSection = ({ item, businessName, selectedColor, bussiness }) => {
  const { t } = useTranslation();
  const currencySymbol = "₹";
  const formatCurrency = (val) =>
    `${currencySymbol} ${Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const subTotal =
    (item?.invoiceId?.startsWith("RT")
      ? item?.returnitems
      : item?.items
    )?.reduce((sum, i) => sum + (i.salePrice || 0) * (i.quantity || 0), 0) || 0;
  const totalTax =
    (item?.invoiceId?.startsWith("RT")
      ? item?.returnitems
      : item?.items
    )?.reduce(
      (sum, i) =>
        sum + (i.salePrice || 0) * (i.quantity || 0) * ((i.igst || 0) / 100),
      0
    ) || 0;
  const totalAmount =
    (item?.invoiceId?.startsWith("RT")
      ? item?.returnitems
      : item?.items
    )?.reduce((sum, i) => sum + (i.finalPrice || 0), 0) || 0;

  const amountReceived = item.amountReceived || 0;
  const balance = totalAmount - amountReceived;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-300 mt-6 px-6 text-sm">
      {/* Left Column */}
      <div className="space-y-4">
        <div
          className={`${selectedColor} text-white font-semibold px-2 py-1 rounded`}
        >
          {t("NEWINVOICE.AMOUNT_IN_WORDS")}
        </div>
        <div className="px-2 py-1 capitalize min-h-[3rem]">
          {item?.transactionAmount
            ? numberToWord(totalAmount) + " Rupees Only"
            : "Amount in words not available"}
        </div>
        <div
          className={`${selectedColor} text-white font-semibold px-2 py-1 rounded`}
        >
          {t("NEWINVOICE.PAYMENT_MODE")}
        </div>
        <div className="px-2 py-1">{item.paymentMode || "-"}</div>
      </div>

      {/* Right Column */}
      <div className="space-y-1">
        <div className="flex justify-between font-normal">
          <span>{t("NEWINVOICE.SUB_TOTAL")}</span>
          <span>{formatCurrency(subTotal)}</span>
        </div>
        {item.cgst > 0 && (
          <div className="flex justify-between font-normal">
            <span>{t("TAX_WITHOUT_PERC.CGST")}</span>
            <span>{formatCurrency(item.cgst)}</span>
          </div>
        )}
        {item.sgst > 0 && (
          <div className="flex justify-between font-normal">
            <span>{t("TAX_WITHOUT_PERC.SGST")}</span>
            <span>{formatCurrency(item.sgst)}</span>
          </div>
        )}
        {item.igst > 0 && (
          <div className="flex justify-between font-normal">
            <span>{t("TAX_WITHOUT_PERC.IGST")}</span>
            <span>{formatCurrency(item.igst)}</span>
          </div>
        )}
        {item.cess > 0 && (
          <div className="flex justify-between font-normal">
            <span>{t("TAX_WITHOUT_PERC.CESS")}</span>
            <span>{formatCurrency(item.cess)}</span>
          </div>
        )}
        <div
          className={`${selectedColor} text-white p-2 flex justify-between font-semibold rounded`}
        >
          <span>{t("NEWINVOICE.TOTAL")}:</span>
          <span>{formatCurrency(totalAmount)}</span>
        </div>
        <div className="flex justify-between font-normal mt-4">
          <span>{t("NEWINVOICE.RECEIVED")}</span>
          <span>{formatCurrency(amountReceived)}</span>
        </div>
        <div className="flex justify-between font-semibold">
          <span>{t("NEWINVOICE.BALANCE")}</span>
          <span>{formatCurrency(balance)}</span>
        </div>
        {bussiness?.aadharPhoto && (
          <div className="flex p-4 bg-gray-100 rounded-lg">
            <img
              src={AWS_URL + bussiness?.aadharPhoto}
              alt="Payment QR Code"
              className="w-30 object-contain"
              //crossOrigin="anonymous"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const InvoiceDesign1 = ({
  item,
  containerRef,
  businessName = "Business Name",
  phone = "0000000000",
  selectedColor = "bg-pink-400",
  darkMode = false,
  bussiness,
}) => {
  const { t } = useTranslation();
  if (!item) return <div>No invoice data available</div>;
  const bgColor = darkMode ? "#170f2a" : "#ffffff";
  const textColor = darkMode ? "#E5E7EB" : "#000000";
  return (
    <div
      className="bg-white shadow-lg print:shadow-none print:bg-white print:w-[210mm] w-[200mm] flex flex-col"
      ref={containerRef}
      style={{
        boxSizing: "border-box",
        padding: 0,
        margin: 0,
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      <InvoiceHeader
        businessName={businessName}
        selectedColor={selectedColor}
        bussiness={bussiness}
      />
      <InvoiceDetails
        businessName={businessName}
        phone={phone}
        invoiceId={item.invoiceId}
        customerName={item.customerName}
        mobile={item.mobile}
        saleDate={item.saleDate}
        selectedColor={selectedColor}
        bussiness={bussiness}
        item={item}
      />
      <InvoiceTable
        items={
          item?.invoiceId?.startsWith("RT") ? item?.returnitems : item?.items
        }
        selectedColor={selectedColor}
      />
      <TotalsSection
        item={item}
        businessName={businessName}
        selectedColor={selectedColor}
        bussiness={bussiness}
      />

      <div className="text-sm mt-6 font-semibold px-6">
        {t("NEWINVOICE.AUTHORIZED_SIGNATORY")}
      </div>
    </div>
  );
};

export default InvoiceDesign1;
